/*
  SPDX-License-Identifier: SEE LICENSE IN EULA.md
  SPDX-FileCopyrightText: 2020-2026 CYBERTEC PostgreSQL International GmbH <office@cybertec.at>
*/
import { QueryKeys } from '../constants/queryKeys';
import { ClusterApiService } from '../services/ClusterApiService';
import { DatasetApiService } from '../services/DatasetApiService';
import { DatasetImportApiService } from '../services/DatasetImportApiService';
import { OrganizationApiService } from '../services/OrganizationApiService';
import { OrganizationUserApiService } from '../services/OrganizationUserApiService';
import { ProjectApiService } from '../services/ProjectApiService';
import { SetupApiService } from '../services/SetupApiService';
import { StorageClassApiService } from '../services/StorageClassApiService';
import { SuperuserClusterApiService } from '../services/SuperuserClusterApiService';
import { SuperuserDatasetApiService } from '../services/SuperuserDatasetApiService';
import { SuperuserDatasetCategoryApiService } from '../services/SuperuserDatasetCategoryApiService';
import { SuperuserJiraApiService } from '../services/SuperuserJiraApiService';
import { SuperuserJiraConfigApiService } from '../services/SuperuserJiraConfigApiService';
import { SuperuserOrganizationApiService } from '../services/SuperuserOrganizationApiService';
import { SuperuserPostgresVersionApiService } from '../services/SuperuserPostgresVersionApiService';
import { SuperuserUserApiService } from '../services/SuperuserUserApiService';
import { SUPlanApiService } from '../services/SUPlanApiService';
import { SupportJiraApiService } from '../services/SupportJiraApiService';
import { SupportPlanApiService } from '../services/SupportPlanApiService';
import { SystemApiService } from '../services/SystemApiService';
import { TeamApiService } from '../services/TeamApiService';
import { UserApiService } from '../services/UserApiService';
import { EntityId } from '../types/api/EntityId';
import { serviceToUseQueryFn, serviceToUseQueryFnData } from './serviceToUseQueryFn';

interface FetchProjectParams {
  organizationId: EntityId;
  projectId: EntityId;
}

interface ClusterIdentification {
  organizationId: EntityId;
  projectId: EntityId;
  clusterId: EntityId;
}

export const fetchProject = {
  key: QueryKeys.fetchProject,
  fn: serviceToUseQueryFnData(({ organizationId, projectId }: FetchProjectParams) =>
    ProjectApiService.fetchProject(organizationId, projectId),
  ),
};

export const fetchClusters = {
  key: QueryKeys.fetchClusters,
  fn: serviceToUseQueryFnData(({ organizationId, projectId }: { organizationId: EntityId; projectId: EntityId }) =>
    ClusterApiService.fetchClusters({ organizationId, projectId }),
  ),
};

export const fetchCluster = {
  key: QueryKeys.fetchCluster,
  fn: serviceToUseQueryFnData((props: { organizationId: EntityId; projectId: EntityId; clusterId: EntityId }) =>
    ClusterApiService.fetchCluster(props),
  ),
};

export const fetchDeepthinkerLatestRuntime = {
  key: QueryKeys.fetchDeepthinkerLatestRuntime,
  fn: serviceToUseQueryFn(({ organizationId, projectId, clusterId }: ClusterIdentification) =>
    ClusterApiService.fetchDeepthinkerLatestRuntime({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchDeepthinkerRunStatus = {
  key: QueryKeys.fetchDeepthinkerRunStatus,
  fn: serviceToUseQueryFn(({ organizationId, projectId, clusterId }: ClusterIdentification) =>
    ClusterApiService.fetchDeepthinkerRunStatus({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchAdvice = {
  key: QueryKeys.fetchAdvice,
  fn: serviceToUseQueryFn(({ clusterId, organizationId, projectId }: ClusterIdentification) =>
    ClusterApiService.fetchAdvice({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchDismissedAdvice = {
  key: QueryKeys.fetchDismissedAdvice,
  fn: serviceToUseQueryFn(({ clusterId, organizationId, projectId }: ClusterIdentification) =>
    ClusterApiService.fetchDismissedAdvice({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchClusterContinuousBackups = {
  key: QueryKeys.fetchClusterContinuousBackups,
  fn: serviceToUseQueryFn(({ clusterId, organizationId, projectId }: ClusterIdentification) =>
    ClusterApiService.fetchClusterContinuousBackups({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchClusterLogicalBackups = {
  key: QueryKeys.fetchClusterLogicalBackups,
  fn: serviceToUseQueryFnData(({ organizationId, projectId, clusterId }: ClusterIdentification) =>
    ClusterApiService.fetchClusterLogicalBackups({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchClusterBackupsHistory = {
  key: QueryKeys.fetchClusterBackupsHistory,
  fn: serviceToUseQueryFnData(({ organizationId, projectId, clusterId }: ClusterIdentification) =>
    ClusterApiService.fetchClusterBackupsHistory({
      organizationId,
      projectId,
      clusterId,
    }),
  ),
};

export const fetchPgHbaOfCluster = {
  key: QueryKeys.fetchPgHbaOfCluster,
  fn: serviceToUseQueryFn(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterPgHba({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchDatabaseListOfCluster = {
  key: QueryKeys.fetchDatabaseListOfCluster,
  fn: serviceToUseQueryFn(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterDatabaseList({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchUserListOfCluster = {
  key: QueryKeys.fetchUserListOfCluster,
  fn: serviceToUseQueryFn(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterUserList({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchClusterDatabaseSchemas = {
  key: QueryKeys.fetchClusterDatabaseSchemas,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
      databaseName,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
      databaseName: string;
    }) =>
      ClusterApiService.fetchClusterDatabaseSchemas({
        organizationId,
        projectId,
        clusterId,
        databaseName,
      }),
  ),
};

export const fetchInvitation = {
  key: QueryKeys.fetchOrganizationInvitations,
  fn: serviceToUseQueryFnData(OrganizationUserApiService.fetchOrganizationInvitations),
};

export const fetchRegistrationInvitation = {
  key: QueryKeys.fetchOrganizationRegistrationInvitations,
  fn: serviceToUseQueryFnData(OrganizationUserApiService.fetchOrganizationRegistrationInvitations),
};

export const fetchDashboardForUser = {
  key: QueryKeys.fetchDashboardForUser,
  fn: serviceToUseQueryFn(UserApiService.fetchDashboardForUser),
};

export const fetchOrganizationUsers = {
  key: QueryKeys.fetchOrganizationUsers,
  fn: serviceToUseQueryFnData(OrganizationUserApiService.fetchOrganizationUsers),
};

export const fetchOidcConfiguration = {
  key: QueryKeys.fetchOidcConfiguration,
  fn: serviceToUseQueryFnData(OrganizationApiService.getOidcConfiguration),
};

export const fetchOrganization = {
  key: QueryKeys.fetchOrganizationById,
  fn: serviceToUseQueryFn(OrganizationApiService.fetchOrganizationById),
};

export const fetchDashboardForOrganization = {
  key: QueryKeys.fetchDashboardForOrganization,
  fn: serviceToUseQueryFn(OrganizationApiService.fetchDashboardForOrganization),
};

export const fetchProjectsOfOrganization = {
  key: QueryKeys.fetchProjectsOfOrganization,
  fn: serviceToUseQueryFn(ProjectApiService.fetchProjects),
};

export const fetchProjectsWithClusters = {
  key: QueryKeys.fetchProjectsWithClusters,
  fn: serviceToUseQueryFn(ProjectApiService.fetchProjectsWithClusters),
};

export const fetchUserServices = {
  key: QueryKeys.fetchUserServices,
  fn: serviceToUseQueryFn(UserApiService.fetchUserServices),
};

export const fetchClusterDetails = {
  key: QueryKeys.fetchClusterDetails,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterDetails({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchClusterStatus = {
  key: QueryKeys.fetchClusterStatus,
  fn: serviceToUseQueryFnData((props: { organizationId: string; projectId: string; clusterId: string }) =>
    ClusterApiService.fetchClusterStatus(props),
  ),
};

export const fetchClusterUpgradeStatus = {
  key: QueryKeys.fetchClusterUpgradeStatus,
  fn: serviceToUseQueryFnData((props: { organizationId: string; projectId: string; clusterId: string }) =>
    ClusterApiService.fetchClusterUpgradeStatus(props),
  ),
};

export const fetchParametersOfCluster = {
  key: QueryKeys.fetchParametersOfCluster,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterParameters({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchNodeListOfCluster = {
  key: QueryKeys.fetchNodeListOfCluster,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterNodeList({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchNodeLogsOfCluster = {
  key: QueryKeys.fetchNodeLogsOfCluster,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
      nodeId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
      nodeId: EntityId;
    }) =>
      ClusterApiService.fetchClusterNodeLogs({
        organizationId,
        projectId,
        clusterId,
        nodeId,
      }),
  ),
};

export const fetchCredentialsOfCluster = {
  key: QueryKeys.fetchCredentialsOfCluster,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterCredentials({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchCredentialsOfClusterCypex = {
  key: QueryKeys.fetchCredentialsOfClusterCypex,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterCredentialsCypex({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchStorageOfCluster = {
  key: QueryKeys.fetchStorageOfCluster,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterStorage({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchDatasets = {
  key: QueryKeys.fetchDatasets,
  fn: serviceToUseQueryFnData(DatasetApiService.fetchDatasets),
};

export const fetchDatasetImports = {
  key: QueryKeys.fetchDatasetImports,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      DatasetImportApiService.fetchClusterDatasetImports({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchSystemConfiguration = {
  key: QueryKeys.fetchSystemConfiguration,
  fn: serviceToUseQueryFnData(SuperuserUserApiService.fetchSystemConfiguration),
};

export const adminFetchUsers = {
  key: QueryKeys.adminFetchUsers,
  fn: serviceToUseQueryFnData(SuperuserUserApiService.fetchUsers),
};

export const adminFetchPlans = {
  key: QueryKeys.adminFetchPlans,
  fn: serviceToUseQueryFnData(SUPlanApiService.fetchPlans),
};

export const fetchAdminJiraConfig = {
  key: QueryKeys.fetchAdminJiraConfig,
  fn: serviceToUseQueryFnData(SuperuserJiraConfigApiService.fetchConfig),
};

export const fetchAdminDatasets = {
  key: QueryKeys.fetchAdminDatasets,
  fn: serviceToUseQueryFnData(SuperuserDatasetApiService.fetchDatasets),
};

export const fetchAdminDatasetsCategories = {
  key: QueryKeys.fetchAdminDatasetsCategories,
  fn: serviceToUseQueryFnData(SuperuserDatasetCategoryApiService.fetchDatasetsCategories),
};

export const adminFetchOrganizations = {
  key: QueryKeys.adminFetchOrganizations,
  fn: serviceToUseQueryFnData(SuperuserOrganizationApiService.fetchOrganizations),
};

export const fetchOrphans = {
  key: QueryKeys.adminFetchOrphans,
  fn: serviceToUseQueryFnData(SuperuserClusterApiService.fetchOrphans),
};

export const fetchLogicalBackupStatus = {
  key: QueryKeys.fetchLogicalBackupStatus,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterLogicalBackupStatus({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchContinuousBackupLatestRestorePoint = {
  key: QueryKeys.fetchContinuousBackupLatestRestorePoint,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterContinuousBackupLatestRestorePoint({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchContinuousBackupOldestRestorePoint = {
  key: QueryKeys.fetchContinuousBackupOldestRestorePoint,
  fn: serviceToUseQueryFnData(
    ({
      organizationId,
      projectId,
      clusterId,
    }: {
      organizationId: EntityId;
      projectId: EntityId;
      clusterId: EntityId;
    }) =>
      ClusterApiService.fetchClusterContinuousBackupOldestRestorePoint({
        organizationId,
        projectId,
        clusterId,
      }),
  ),
};

export const fetchSupportPlans = {
  key: QueryKeys.fetchSupportPlans,
  fn: serviceToUseQueryFnData(SupportPlanApiService.fetchSupportPlans),
};

export const fetchJiraRequestTypes = {
  key: QueryKeys.fetchJiraRequestTypes,
  fn: serviceToUseQueryFnData(SupportJiraApiService.fetchRequestTypes),
};

export const fetchJiraRequestTypeFields = {
  key: QueryKeys.fetchJiraRequestTypeFields,
  fn: serviceToUseQueryFnData(({ id }: { id: string }) => SupportJiraApiService.fetchRequestTypeFields({ id })),
};

export const fetchJiraRequest = {
  key: QueryKeys.fetchJiraRequest,
  fn: serviceToUseQueryFnData(({ id }: { id: string }) => SupportJiraApiService.fetchRequest({ id })),
};

export const fetchJiraRequests = {
  key: QueryKeys.fetchJiraRequests,
  fn: serviceToUseQueryFnData(
    ({ requestStatus, take, skip }: { requestStatus: 'all' | 'closed' | 'open'; take: number; skip: number }) =>
      SupportJiraApiService.fetchRequestsWithPagination({
        requestStatus,
        take,
        skip,
      }),
  ),
};

export const fetchJiraRequestComments = {
  key: QueryKeys.fetchJiraRequestComments,
  fn: serviceToUseQueryFnData(({ id }: { id: string }) => SupportJiraApiService.fetchRequestComments({ id })),
};

export const fetchJiraConfig = {
  key: QueryKeys.fetchJiraConfig,
  fn: serviceToUseQueryFnData(SupportJiraApiService.fetchJiraConfig),
};

export const fetchAdminJiraTickets = {
  key: QueryKeys.fetchAdminJiraTickets,
  fn: serviceToUseQueryFnData(
    ({ requestStatus, take, skip }: { requestStatus: 'all' | 'closed' | 'open'; take: number; skip: number }) =>
      SuperuserJiraApiService.fetchCustomerRequestsWithPagination({
        requestStatus,
        take,
        skip,
      }),
  ),
};

export const fetchPostgresVersions = {
  key: QueryKeys.fetchPostgresVersions,
  fn: serviceToUseQueryFnData(SuperuserPostgresVersionApiService.fetchPostgresVersions),
};

export const fetchSystemStatus = {
  key: QueryKeys.fetchSystemStatus,
  fn: serviceToUseQueryFn(SystemApiService.fetchStatus),
};

export const fetchTeams = {
  key: QueryKeys.fetchTeams,
  fn: serviceToUseQueryFnData(TeamApiService.fetchTeams),
};

export const fetchTeam = {
  key: QueryKeys.fetchTeam,
  fn: serviceToUseQueryFnData(TeamApiService.fetchTeam),
};

export const fetchSsoGroups = {
  key: QueryKeys.fetchSsoGroups,
  fn: serviceToUseQueryFnData(TeamApiService.fetchSsoGroups),
};

export const fetchStorageClasses = {
  key: QueryKeys.fetchStorageClasses,
  fn: serviceToUseQueryFn(StorageClassApiService.fetchStorageClasses),
};

export const fetchSetupProgress = {
  key: QueryKeys.fetchSetupProgress,
  fn: serviceToUseQueryFn(SetupApiService.fetchProgress),
};
